export const ROLE_ORDER = [
  'Executive Sponsor',
  'RAPID Lead',
  'CPI / Black Belt',
  'Data SME',
  'Pod Lead',
];

export const DETAIL_TABS = [
  ['overview', 'Overview'],
  ['intake', 'Intake'],
  ['criteria', 'Criteria'],
  ['qa', 'Readiness QA'],
  ['approval', 'Approval & Delegation'],
  ['pods', 'Pods'],
  ['diagnose', 'Diagnosis'],
  ['canvas', 'Scope Canvas'],
  ['actions', 'Actions'],
  ['metrics', 'Metrics & Value'],
  ['governance', 'Governance'],
  ['closeout', 'Closeout / Handoff'],
  ['shells', 'Roadmap & Scope'],
];

const DETAIL_TAB_KEYS = new Set(DETAIL_TABS.map(([key]) => key));

export const DETAIL_TAB_LABELS = Object.fromEntries(DETAIL_TABS);

// AC-RAPID-3: the 13 detail tabs regroup into the 6 lifecycle phases
// (FS-RAPID-2). Phases are the primary nav; every existing tab stays reachable
// nested inside its phase, and old ?tab= deep links map to the right phase.
export const DETAIL_PHASES = [
  ['intake', 'Intake', ['overview', 'intake']],
  ['qualify', 'Qualify', ['criteria', 'qa', 'approval']],
  ['diagnose', 'Diagnose', ['diagnose']],
  ['execute', 'Execute', ['pods', 'canvas', 'actions']],
  ['value', 'Value & Trust', ['metrics']],
  ['govern', 'Govern & Close', ['governance', 'closeout', 'shells']],
];

export function phaseForDetailTab(tabKey) {
  const match = DETAIL_PHASES.find(([, , tabKeys]) => tabKeys.includes(tabKey));
  return match ? match[0] : DETAIL_PHASES[0][0];
}

const TOUR_STEP_TO_TAB = {
  3: 'intake',
  4: 'intake',
  5: 'qa',
  6: 'criteria',
  7: 'overview',
  8: 'pods',
  9: 'pods',
  10: 'canvas',
  11: 'diagnose',
  12: 'metrics',
  13: 'governance',
  14: 'closeout',
  15: 'closeout',
  16: 'shells',
};

export const RAPID_PHASE_RAIL = [
  ['intake', 'Intake'],
  ['scope', 'Scope'],
  ['diagnose', 'Diagnose'],
  ['implement', 'Implement'],
  ['closeout', 'Closeout / Handoff'],
  ['graduated', 'Graduation Preview'],
];

export const RAPID_KANBAN_COLUMNS = [
  ['todo', 'To Do'],
  ['doing', 'In Progress'],
  ['blocked', 'Blocked'],
  ['done', 'Done'],
];

const DRIVER_PARENT_FALLBACK = Object.freeze({
  'P1-D2': 'P1-D1',
  'P1-D3': 'P1-D2',
  'P1-D4': 'P1-D2',
  'P1-D5': 'P1-D2',
  'P1-D6': 'P1-D2',
  'P1-D7': 'P1-D2',
  'P1-D8': 'P1-D5',
  'P1-D9': 'P1-D5',
  'P1-D10': 'P1-D5',
  'P1-D11': 'P1-D5',
  'P1-D12': 'P1-D5',
  'P1-D13': 'P1-D5',
  'P2-D2': 'P2-D1',
  'P2-D3': 'P2-D2',
  'P2-D4': 'P2-D2',
  'P2-D5': 'P2-D4',
  'P2-D6': 'P2-D4',
  'P2-D7': 'P2-D4',
  'P2-D8': 'P2-D4',
  'P2-D9': 'P2-D4',
  'P2-D10': 'P2-D4',
  'P2-D11': 'P2-D4',
});

const DRIVER_TIER_LABELS = Object.freeze({
  1: 'Outcome',
  2: 'Primary Driver',
  3: 'Operational Driver',
  4: 'Contributing Driver',
});

function scoreRecommendation(total, threshold = 45, rows = []) {
  if (total < threshold) return 'Route to other OWHA/DHA work queue';
  const items = deriveOwnerConfirmationItems(rows);
  // Never claim caveats that don't exist (codex-audit P2).
  if (!items.length) return 'Qualifies for RAPID.';
  const named = items
    .map((item) => (item.code ? `${item.criterion} (${item.code})` : item.criterion))
    .join(', ');
  return `Qualifies for RAPID; owner confirmation required for ${named}.`;
}

// External UAT FINDING-010: a plural "owner-confirmation items" summary must
// name the items it implies. The items live in the criteria rows themselves —
// rows whose visible text flags an owner-confirmation caveat (OCI-…). The
// separator class tolerates spaces, underscores, and the Unicode dash range.
const OWNER_CONFIRMATION_PATTERN = /owner[\s_‐-―-]?confirmation|\bOCI-\d+\b/i;

export function deriveOwnerConfirmationItems(rows = []) {
  const items = [];
  for (const row of rows) {
    const text = `${row.description || ''} ${row.rationale || ''} ${row.evidenceNote || ''}`;
    if (!OWNER_CONFIRMATION_PATTERN.test(text)) continue;
    const code = text.match(/\bOCI-\d+\b/i);
    items.push({ criterion: row.criterion, code: code ? code[0].toUpperCase() : null });
  }
  return items;
}

// Render-time replacement for a stored generic recommendation string: when the
// score qualifies and the rows identify owner-confirmation items, name them.
export function formatCriteriaRecommendation(criteriaScore = {}) {
  const recommendation = criteriaScore.recommendation || '';
  const total = Number(criteriaScore.total);
  const threshold = Number(criteriaScore.threshold) || 45;
  if (!Number.isFinite(total) || total < threshold) return recommendation;
  const items = deriveOwnerConfirmationItems(criteriaScore.rows || []);
  if (!items.length) return recommendation;
  const named = items
    .map((item) => (item.code ? `${item.criterion} (${item.code})` : item.criterion))
    .join(', ');
  return `Qualifies for RAPID; owner confirmation required for ${named}.`;
}

export function slugify(value = '') {
  return (
    String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'item'
  );
}

export function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function joinLines(values = []) {
  return (values || []).filter(Boolean).join('\n');
}

export function parseLines(value = '') {
  return String(value)
    .split(/\r?\n/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function calculateRapidCriteria(rows = []) {
  const nextRows = rows.map((row) => ({
    ...row,
    selectedScore: Number(row.selectedScore || 0),
    weight: Number(row.weight || 0),
    weightedContribution: Number(
      (Number(row.selectedScore || 0) * Number(row.weight || 0)).toFixed(1),
    ),
  }));
  const total = Number(
    nextRows.reduce((sum, row) => sum + Number(row.weightedContribution || 0), 0).toFixed(1),
  );
  return {
    rows: nextRows,
    total,
    threshold: 45,
    recommendation: scoreRecommendation(total, 45, nextRows),
  };
}

export function summarizePodHealth(pod) {
  const contributors = Number(pod?.activeContributorCount || pod?.staffingCount || 0);
  if (contributors > 20) return 'Too large / narrow contributors';
  if (contributors < 10) return 'Likely missing expertise';
  return 'Healthy sizing';
}

export function formatLongDate(value) {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export function formatMoney(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `$${Number(value).toLocaleString()}`;
}

export function podDisplayName(podId) {
  if (!podId) return 'Cross-pod';
  return String(podId).replace(/^POD-(\d+)$/i, 'Pod $1');
}

function parseNumericValue(value) {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatNumericGap(gap, unitHint = '') {
  if (gap == null) return '—';
  const rounded = Number(gap.toFixed(1));
  if (unitHint.includes('%')) return `${rounded}%`;
  return `${rounded}`;
}

export function buildPhaseRail(rapid) {
  const activeIndex = Math.max(
    0,
    RAPID_PHASE_RAIL.findIndex(([phaseId]) => phaseId === rapid?.phase),
  );
  return RAPID_PHASE_RAIL.map(([phaseId, label], index) => ({
    phaseId,
    label,
    state: index < activeIndex ? 'complete' : index === activeIndex ? 'active' : 'upcoming',
  }));
}

export function deriveBowlerRows(bowlerChart) {
  return (bowlerChart?.monthly || []).map((row) => {
    const actual = parseNumericValue(row.actual);
    const target = parseNumericValue(row.target);
    const gap = actual == null || target == null ? null : Number((actual - target).toFixed(1));
    let rag = 'Unknown';
    if (gap != null) {
      rag = gap >= 0 ? 'Green' : gap >= -10 ? 'Amber' : 'Red';
    }
    return {
      ...row,
      kind: row.forecast || row.status === 'forecast' ? 'Forecast' : 'Actual',
      gap,
      rag,
    };
  });
}

export function actionDueState(action, rapid) {
  if (action?.status === 'blocked' || String(action?.health).toLowerCase() === 'red') {
    return { tone: 'danger', label: 'Blocked' };
  }
  const dueDate = action?.dueDate
    ? new Date(`${String(action.dueDate).slice(0, 10)}T12:00:00`)
    : null;
  const now = rapid?.timeline?.currentDemoDate
    ? new Date(`${String(rapid.timeline.currentDemoDate).slice(0, 10)}T12:00:00`)
    : null;
  if (dueDate && now && dueDate < now) return { tone: 'danger', label: 'Overdue' };
  if (dueDate && now) {
    const diffDays = Math.round((dueDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
    if (diffDays <= 7) return { tone: 'warning', label: 'Due soon' };
  }
  return { tone: 'neutral', label: 'On track' };
}

export function buildDriverNodes(pod) {
  return (pod?.driverNodes || []).map((node) => ({
    ...node,
    parentId: node.parentId || DRIVER_PARENT_FALLBACK[node.id] || null,
  }));
}

export function buildDriverColumns(pod) {
  const nodes = buildDriverNodes(pod);
  return [1, 2, 3, 4].map((tier) => ({
    tier,
    label: DRIVER_TIER_LABELS[tier] || `Tier ${tier}`,
    nodes: nodes.filter((node) => Number(node.tier) === tier),
  }));
}

export function buildLineagePreviewNodes(detail) {
  const requestId = detail?.sourceRequest?.id || 'Request';
  const requestTitle = detail?.sourceRequest?.title || detail?.sourceRequest?.summary || 'Request';
  const closeout = detail?.rapid?.closeout || {};
  const graduation = closeout?.graduation || {};
  const projectPreviewId =
    detail?.projectPreviewRecord?.id || graduation?.projectId || 'Project preview';
  const handoffOwner =
    closeout?.handoffOwner || detail?.rapid?.functionalSponsorLabel || 'Handoff owner pending';

  return [
    {
      id: 'request',
      testId: 'rapid-lineage-node-request',
      kind: 'Request',
      label: requestId,
      detail: requestTitle,
      edgeLabel: 'Carried forward',
      edgeTestId: 'rapid-lineage-edge-request',
      evidence: {
        title: requestId,
        summary: requestTitle,
        source: 'RAPID request',
        owner: detail?.rapid?.functionalSponsorLabel || 'Functional sponsor',
      },
    },
    {
      id: 'intake',
      kind: 'RFI / Intake',
      label: 'Intake extraction',
      detail: detail?.rapid?.expectedExtraction?.title || 'Standard (safe) intake summary',
      edgeLabel: 'Carried forward',
    },
    {
      id: 'score',
      testId: 'rapid-lineage-node-score',
      kind: 'Score',
      label: `${Number(detail?.rapid?.criteriaScore?.total || 0).toFixed(1)} / ${detail?.rapid?.criteriaScore?.threshold || 45}`,
      detail: detail?.rapid?.criteriaScore?.recommendation || 'Criteria decision',
      edgeLabel: 'Carried forward',
      evidence: {
        title: 'Criteria score',
        summary: detail?.rapid?.criteriaScore?.recommendation || 'Criteria score preview',
        source: 'Criteria matrix',
        owner: detail?.rapid?.rapidLeadLabel || 'RAPID lead',
      },
    },
    {
      id: 'delegation',
      kind: 'Delegation Memo',
      label: 'Delegation memo',
      detail: detail?.rapid?.approval?.delegationMemo?.memoStatus || 'Memo preview',
      edgeLabel: 'Carried forward',
    },
    {
      id: 'canvas',
      testId: 'rapid-lineage-node-canvas',
      kind: 'Scope Canvas',
      label: 'Scope Canvas / Placemat',
      detail:
        detail?.rapid?.canvas?.problemStatement ||
        detail?.rapid?.problemStatement ||
        'Scope Canvas context',
      edgeLabel: 'Carried forward',
      evidence: {
        title: 'Scope Canvas / Placemat',
        summary:
          detail?.rapid?.canvas?.problemStatement ||
          detail?.rapid?.problemStatement ||
          'Canvas context',
        source: 'ART-RAPID-V2-CANVAS',
        owner: detail?.rapid?.rapidLeadLabel || 'RAPID lead',
      },
    },
    {
      id: 'driver',
      testId: 'rapid-lineage-node-driver',
      kind: 'Driver Tree',
      label: 'Pod driver trees',
      detail: `${detail?.pods?.length || 0} pod workspaces`,
      edgeLabel: 'Carried forward',
      evidence: {
        title: 'Driver tree lineage',
        summary: `${detail?.pods?.length || 0} pod workspaces with deterministic driver nodes.`,
        source: 'Pod driver structures',
        owner: detail?.rapid?.rapidLeadLabel || 'RAPID lead',
      },
    },
    {
      id: 'brief',
      testId: 'rapid-lineage-node-brief',
      kind: 'Brief',
      label: detail?.briefings?.[0]?.id || 'Governance brief',
      detail: detail?.briefings?.[0]?.title || 'Structured governance preview',
      edgeLabel: 'Carried forward',
    },
    {
      id: 'decision',
      testId: 'rapid-lineage-node-decision',
      kind: 'Decision',
      label: detail?.decisions?.[0]?.id || 'Decision log',
      detail:
        detail?.decisions?.[0]?.title || detail?.decisions?.[0]?.decision || 'Decision continuity',
      edgeLabel: 'Carried forward',
    },
    {
      id: 'handoff',
      testId: 'rapid-lineage-node-handoff',
      kind: 'Handoff',
      label: 'Handoff package',
      detail: `Owner: ${handoffOwner}`,
      edgeLabel: 'Carried forward',
      evidence: {
        title: 'Handoff preview',
        summary: `Closeout readiness ${closeout?.closeoutReadiness?.percent || 0}% with handoff to ${handoffOwner}.`,
        source: 'Closeout package',
        owner: handoffOwner,
      },
    },
    {
      id: 'project-preview',
      testId: 'rapid-lineage-node-project-preview',
      kind: 'Project Preview',
      label: projectPreviewId,
      detail: 'PRIME-derived preview',
      evidence: {
        title: projectPreviewId,
        summary:
          detail?.projectPreviewRecord?.originNarrative ||
          detail?.projectPreviewRecord?.description ||
          'PRIME-derived preview',
        source: 'Project preview',
        owner: detail?.rapid?.executiveSponsorLabel || 'Executive sponsor',
      },
    },
  ].filter(Boolean);
}

export function detailTabFromSearch(searchParams) {
  const requested = searchParams.get('tab');
  return DETAIL_TAB_KEYS.has(requested) ? requested : 'overview';
}

export function buildDetailSearch(searchParams, nextTab) {
  const next = new URLSearchParams(searchParams);
  if (!DETAIL_TAB_KEYS.has(nextTab) || nextTab === 'overview') next.delete('tab');
  else next.set('tab', nextTab);
  return next;
}

export function tourRouteForStep(step, rapidId) {
  if (step <= 2) return '/rapids';
  return `/rapids/${rapidId}?tab=${TOUR_STEP_TO_TAB[step] || 'overview'}`;
}

export function tabForTourStep(step) {
  return TOUR_STEP_TO_TAB[step] || null;
}

export function rapidHealthOrder(left, right) {
  const order = { red: 0, amber: 1, yellow: 1, green: 2, gray: 3, grey: 3 };
  return (
    (order[String(left.healthOverall || '').toLowerCase()] ?? 99) -
      (order[String(right.healthOverall || '').toLowerCase()] ?? 99) ||
    Number(left.priorityRank || 999) - Number(right.priorityRank || 999)
  );
}

export function buildDerivedActions(detail, detailState) {
  const byId = new Map();
  for (const action of [...(detail.workItems || []), ...(detailState.createdActions || [])]) {
    if (!action?.id) continue;
    byId.set(action.id, action);
  }
  return [...byId.values()];
}

function mergeById(seedRecords = [], localRecords = []) {
  const byId = new Map();
  for (const record of [...seedRecords, ...localRecords]) {
    if (!record?.id) continue;
    byId.set(record.id, record);
  }
  return [...byId.values()];
}

export function buildDerivedRfIs(rapid, detailState) {
  return mergeById(rapid.expectedExtraction?.rfis || [], detailState.createdRfIs || []);
}

export function buildDerivedAssumptions(rapid, detailState) {
  const seeded = (rapid.canvas?.assumptions || []).map((text, index) => ({
    id: `ASSUMPTION-SEED-${String(index + 1).padStart(3, '0')}`,
    text,
    sourceFindingId: null,
    status: 'Baseline',
  }));
  return mergeById(seeded, detailState.createdAssumptions || []);
}

export function buildDerivedBarriers(rapid, detailState) {
  return mergeById(rapid.barriers?.barriers || [], detailState.createdBarriers || []);
}

export function buildDerivedDecisions(detail, detailState) {
  return mergeById(detail.decisions || [], detailState.createdDecisions || []);
}

export function buildDerivedDataIssues(detailState) {
  return detailState.createdDataIssues || [];
}

export function acceptedCloseoutStatus(status) {
  return ['Accepted', 'Accepted with follow-up', 'Graduated to project'].includes(status);
}

export function buildCloseoutChecklist(closeoutState) {
  return (closeoutState.checklist || []).map((item) => {
    if (item.item === 'Sponsor validation recorded') {
      return { ...item, done: acceptedCloseoutStatus(closeoutState.validationStatus) };
    }
    if (item.item === 'Handoff owner assigned') {
      return { ...item, done: Boolean(closeoutState.handoffOwner?.trim()) };
    }
    return item;
  });
}

export function buildDerivedActivity(detail, detailState) {
  return [...(detailState.activityEntries || []), ...(detail.rapid.activityFeed?.feed || [])];
}

export function findRoleData(roleViews, role) {
  return roleViews?.roles?.[role] || null;
}

export function roleRecommendedAction(roleData) {
  if (!roleData) return null;
  if (roleData.recommendedAction) return roleData.recommendedAction;
  const perPod = Object.keys(roleData)
    .filter((key) => /^recommendedActionPod\d+$/i.test(key))
    .sort()
    .map((key) => {
      const podLabel = key.replace(/^recommendedAction/i, '').replace(/Pod(\d+)/i, 'Pod $1');
      return `${podLabel}: ${roleData[key]}`;
    });
  return perPod.length ? perPod.join('  •  ') : null;
}

export function buildMetricsTrustGaps(detail, trust, dataIssues) {
  const findingIds = new Set((detail.rapid.readinessQa?.findings || []).map((f) => f.id));
  const dataDefIds = trust.dataDefinition.map((m) => m.id).join(' · ');
  const needsConfirmationCount = Number(
    trust?.counts?.needsConfirmation ?? trust.needsConfirmation.length,
  );
  const dataDefinitionCount = Number(trust?.counts?.dataDefinition ?? trust.dataDefinition.length);
  const dataDefinitionExamples = dataDefIds ? `; page examples: ${dataDefIds}` : '';
  const gaps = [
    {
      testId: 'trust-gap-denominator',
      title: 'Eligible-population denominator is unconfirmed',
      why: 'A Pod 1 driver references a denominator that has not been confirmed for Active-Duty/enterprise scope.',
      blocks:
        'Blocks briefing virtual-PHA utilization and any denominator-dependent metric as authoritative.',
      owner: 'Pod 1 Lead / Data SME',
      statusLabel: 'Needs confirmation',
      linkedId: findingIds.has('QA-003') ? 'QA-003 · M-P1-007' : 'M-P1-007',
    },
    {
      testId: 'trust-gap-source-owner',
      title: 'Source-system owners not confirmed for all metrics',
      why: `${needsConfirmationCount} metrics still lack a confirmed authoritative source owner.`,
      blocks: 'Blocks a single trusted operating picture and a production value claim.',
      owner: 'RAPID Lead / Data SME',
      statusLabel: 'Needs confirmation',
      linkedId: 'B-26-001',
    },
    {
      testId: 'trust-gap-slot-time',
      title: 'Virtual PHA slot-time assumption not validated',
      why: 'Slot-time savings drive a labor-hour bucket in the value model but the assumption is unvalidated.',
      blocks: 'Blocks production confidence in the cost-avoidance estimate.',
      owner: 'Pod 1 Lead',
      statusLabel: 'Assumption-based estimate',
      linkedId: 'M-P1-005',
    },
    {
      testId: 'trust-gap-lab-standing-orders',
      title: 'Lab standing-order availability gap',
      why: 'Standing-order access varies across lab workflows and may need policy or workflow intervention.',
      blocks: 'Blocks reliable ancillary IMR closure for labs.',
      owner: 'Pod 2 Lead',
      statusLabel: 'Watch',
      linkedId: 'M-P2-004 · B-26-003',
    },
    {
      testId: 'trust-gap-service-systems',
      title: 'By-Service readiness systems outside GENESIS',
      why: 'Service-specific readiness data sits outside one unified workflow with unclear ownership.',
      blocks: 'Blocks an enterprise-normalized readiness picture.',
      owner: 'Service readiness offices',
      statusLabel: 'Needs confirmation',
      linkedId: 'M-P2-007',
    },
    {
      testId: 'trust-gap-data-definition',
      title: 'Data-definition metrics remain open',
      why: `${dataDefinitionCount} data-definition metrics are not yet defined for briefing${dataDefinitionExamples}.`,
      blocks: 'Blocks treating governance/denominator metrics as final.',
      owner: 'Data SME',
      statusLabel: 'Needs confirmation',
      linkedId: dataDefIds,
    },
  ];

  (dataIssues || []).forEach((issue) => {
    gaps.push({
      testId: `trust-gap-issue-${issue.id}`,
      title: issue.title || issue.id,
      why: issue.summary || issue.uiCopy || 'Converted data issue requiring resolution.',
      blocks: 'Blocks production validation until resolved.',
      owner: issue.owner || 'RAPID Lead',
      statusLabel: issue.status || 'Open',
      linkedId: issue.id,
    });
  });

  return gaps;
}

export function nowTimestamp() {
  const date = new Date();
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
